import { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, FileText, Receipt, Plus, Eye, ArrowRight, MagnifyingGlass, CheckCircle, Package, DownloadSimple, UserPlus, Printer, PencilSimple, Trash, PaperPlaneTilt } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency, useCompany } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import SignaturePad from '../../components/SignaturePad';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../../components/DraftBanner';
import LayoutPreviewButton from '../../components/LayoutPreviewButton';
import SendEmailModal from '../../components/SendEmailModal';
import QuotationAttachmentField from '../../components/common/QuotationAttachmentField';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import DocSummary from "../../components/DocSummary";

const API = process.env.REACT_APP_BACKEND_URL;

// VAT treatment dropdown options (LPO/PI/GRN/etc). "inclusive" means the
// unit price already contains VAT so no 5% is added on top; the backend
// derives an informational VAT portion but keeps the total unchanged.
const VAT_TREATMENT_OPTIONS = [
  { value: 'standard',     label: 'Standard 5% VAT',      rate: 5 },
  { value: 'zero_rated',   label: 'Zero Rated (0%)',      rate: 0 },
  { value: 'exempt',       label: 'Exempt (0%)',          rate: 0 },
  { value: 'out_of_scope', label: 'Out of Scope (0%)',    rate: 0 },
  { value: 'inclusive',    label: 'VAT Included in Price',rate: 5 }, // rate used only for info back-calc
  { value: 'custom',       label: 'Custom %',             rate: null },
];

function applyVatTreatment(line, treatment) {
  const opt = VAT_TREATMENT_OPTIONS.find(o => o.value === treatment) || VAT_TREATMENT_OPTIONS[0];
  const next = { ...line, vat_treatment: treatment };
  if (opt.rate !== null) next.tax_rate = opt.rate;
  return next;
}

function computeLineTotal(line) {
  const base = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
  const treatment = (line.vat_treatment || 'standard').toLowerCase();
  if (treatment === 'inclusive' || treatment === 'zero_rated' || treatment === 'exempt' || treatment === 'out_of_scope') {
    return base;
  }
  return base * (1 + (parseFloat(line.tax_rate) || 0) / 100);
}

export default function PurchasesPage() {
  const { token, user } = useAuth();
  const { formatCurrency } = useCurrency();
  const { logoUrl, company } = useCompany();
  const [activeTab, setActiveTab] = useState('orders');
  const [rfqs, setRfqs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showRfqModal, setShowRfqModal] = useState(false);
  const [showPoModal, setShowPoModal] = useState(false);
  const [showGrnModal, setShowGrnModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNewSupplierInline, setShowNewSupplierInline] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingPO, setEditingPO] = useState(null);
  const [editingRFQ, setEditingRFQ] = useState(null);
  const [editingGRN, setEditingGRN] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocType, setDeleteDocType] = useState('po'); // 'po', 'rfq', or 'grn'
  const [deletionReason, setDeletionReason] = useState('');
  const [editApprovalReason, setEditApprovalReason] = useState('');
  const [showEditApprovalPrompt, setShowEditApprovalPrompt] = useState(false);
  const [pendingEditPayload, setPendingEditPayload] = useState(null);
  const [emailModal, setEmailModal] = useState({ open: false, docType: '', docId: '', docNumber: '', supplierEmail: '', supplierName: '' });
  
  // New supplier form
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  // Form states
  const [formData, setFormData] = useState({
    supplier_id: '',
    date: new Date().toISOString().split('T')[0],
    valid_until: '',
    expected_date: '',
    po_id: '',
    vat_category: 'standard', // UAE VAT Category
    notes: '',
    lines: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 5, vat_treatment: 'standard', specifications: '', unit: 'pcs' }], // Default 5% VAT
    // New fields for RFQ
    delivery_required_by: '',
    terms_conditions: '',
    payment_terms: '',
    delivery_location: '',
    signature: '',
    project_reference: '',
    project_location: '',
    layout_template: '',
    // PO-specific extended fields
    warranty_clause: '',
    special_clauses: '',
    discount_adjustment: 0,
    discount_adjustment_note: '',
    attachments: [],   // Array of attachment ids fetched via /api/documents/attachments
  });

  // Draft auto-save: scoped per modal type; only when creating new (not editing)
  const rfqDraft = useDraftAutosave({
    key: 'purchases-rfq', data: formData,
    enabled: showRfqModal && !editingRFQ, userId: user?.id,
  });
  const poDraft = useDraftAutosave({
    key: 'purchases-po', data: formData,
    enabled: showPoModal && !editingPO, userId: user?.id,
  });
  const grnDraft = useDraftAutosave({
    key: 'purchases-grn', data: formData,
    enabled: showGrnModal && !editingGRN, userId: user?.id,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [rfqRes, poRes, grnRes, suppRes] = await Promise.all([
        fetch(`${API}/api/purchase/rfq`, { headers }),
        fetch(`${API}/api/purchase/orders`, { headers }),
        fetch(`${API}/api/purchase/grn`, { headers }),
        fetch(`${API}/api/contacts?contact_type=supplier`, { headers })
      ]);
      
      if (rfqRes.ok) setRfqs(await rfqRes.json());
      if (poRes.ok) setOrders(await poRes.json());
      if (grnRes.ok) setGrns(await grnRes.json());
      else setGrns([]); // GRN endpoint might not exist yet
      if (suppRes.ok) setSuppliers(await suppRes.json());
    } catch (err) {
      toast.error('Failed to load purchase data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      date: new Date().toISOString().split('T')[0],
      valid_until: '',
      expected_date: '',
      po_id: '',
      vat_category: 'standard',
      notes: '',
      lines: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 5, vat_treatment: 'standard', specifications: '', unit: 'pcs' }],
      // New fields
      delivery_required_by: '',
      terms_conditions: '',
      payment_terms: '',
      delivery_location: '',
      signature: '',
      project_reference: '',
      project_location: '',
      layout_template: '',
      warranty_clause: '',
      special_clauses: '',
      discount_adjustment: 0,
      discount_adjustment_note: '',
      attachments: [],
    });
  };

  const addLine = () => {
    const defaultRate = (formData.vat_category === 'standard') ? 5 : 0;
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit_price: 0, tax_rate: defaultRate, specifications: '', unit: 'pcs' }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const removeLine = (index) => {
    setFormData(prev => {
      if (prev.lines.length === 1) return prev;
      return {
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      };
    });
  };

  const calculateTotal = () => {
    return formData.lines.reduce((sum, line) => {
      const lineTotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
      const tax = lineTotal * ((parseFloat(line.tax_rate) || 0) / 100);
      return sum + lineTotal + tax;
    }, 0);
  };

  // Create new supplier inline
  const handleCreateSupplier = async () => {
    if (!newSupplier.name) {
      toast.error('Supplier name is required');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...newSupplier,
          contact_type: 'supplier',
          currency: company?.currency || 'AED'
        })
      });
      
      if (res.ok) {
        const created = await res.json();
        toast.success('Supplier created');
        setSuppliers([...suppliers, created]);
        setFormData({ ...formData, supplier_id: created.id });
        setShowNewSupplierInline(false);
        setNewSupplier({ name: '', company_name: '', email: '', phone: '', address: '' });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create supplier');
      }
    } catch (err) {
      toast.error('Failed to create supplier');
    }
  };

  // RFQ - No pricing, just items and quantities
  const handleCreateRfq = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/purchase/rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          supplier_id: formData.supplier_id,
          date: formData.date,
          valid_until: formData.valid_until || null,
          notes: formData.notes || null,
          lines: formData.lines.map(l => ({
            description: l.description,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: 0, // RFQ doesn't have price - supplier will quote
            tax_rate: 0,
            specifications: l.specifications || null,
            unit: l.unit || 'pcs'
          })),
          // New fields
          delivery_required_by: formData.delivery_required_by || null,
          terms_conditions: formData.terms_conditions || null,
          payment_terms: formData.payment_terms || null,
          delivery_location: formData.delivery_location || null,
          signature: formData.signature || null
        })
      });
      
      if (res.ok) {
        toast.success('RFQ created successfully');
        setShowRfqModal(false);
        rfqDraft.clear();
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create RFQ');
      }
    } catch (err) {
      toast.error('Failed to create RFQ');
    }
  };

  // Edit RFQ - open modal with pre-filled data
  const handleEditRFQ = (rfq) => {
    setEditingRFQ(rfq);
    setFormData({
      supplier_id: rfq.supplier_id,
      date: rfq.date,
      valid_until: rfq.valid_until || '',
      expected_date: '',
      po_id: '',
      vat_category: 'standard',
      notes: rfq.notes || '',
      lines: rfq.lines && rfq.lines.length > 0
        ? rfq.lines.map(line => ({
            description: line.description || '',
            quantity: line.quantity ?? 0,
            unit_price: line.unit_price ?? 0,
            tax_rate: line.tax_rate || 0,
            specifications: line.specifications || '',
            unit: line.unit || 'pcs'
          }))
        : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, specifications: '', unit: 'pcs' }],
      // New fields
      delivery_required_by: rfq.delivery_required_by || '',
      terms_conditions: rfq.terms_conditions || '',
      payment_terms: rfq.payment_terms || '',
      delivery_location: rfq.delivery_location || '',
      signature: rfq.signature || ''
    });
    setShowRfqModal(true);
  };

  // Update RFQ
  const handleUpdateRFQ = async (e) => {
    e.preventDefault();
    if (!editingRFQ) return;
    
    try {
      const res = await fetch(`${API}/api/purchase/rfq/${editingRFQ.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          supplier_id: formData.supplier_id,
          date: formData.date,
          valid_until: formData.valid_until || null,
          notes: formData.notes || null,
          lines: formData.lines.map(l => ({
            description: l.description,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: 0,
            tax_rate: 0,
            specifications: l.specifications || null,
            unit: l.unit || 'pcs'
          })),
          // New fields
          delivery_required_by: formData.delivery_required_by || null,
          terms_conditions: formData.terms_conditions || null,
          payment_terms: formData.payment_terms || null,
          delivery_location: formData.delivery_location || null,
          signature: formData.signature || null
        })
      });
      
      if (res.ok) {
        toast.success('RFQ updated successfully');
        setShowRfqModal(false);
        setEditingRFQ(null);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update RFQ');
      }
    } catch (err) {
      toast.error('Failed to update RFQ');
    }
  };

  // Delete RFQ
  const handleDeleteRFQ = async () => {
    if (!documentToDelete) return;
    
    try {
      const res = await fetch(`${API}/api/purchase/rfq/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('RFQ deleted successfully');
        setShowDeleteConfirm(false);
        setDocumentToDelete(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete RFQ');
      }
    } catch (err) {
      toast.error('Failed to delete RFQ');
    }
  };

  const handleCreatePo = async (e) => {
    e.preventDefault();
    // Client-side validation — surface clear toast instead of silent no-op
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    const validLines = (formData.lines || []).filter(
      (l) => (l.description || '').trim() && (parseFloat(l.quantity) || 0) > 0
    );
    if (validLines.length === 0) {
      toast.error('Add at least one line item with a description and quantity');
      return;
    }
    try {
      const res = await fetch(`${API}/api/purchase/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          supplier_id: formData.supplier_id,
          date: formData.date,
          expected_date: formData.expected_date || null,
          delivery_required_by: formData.delivery_required_by || null,
          vat_category: formData.vat_category,
          notes: formData.notes || null,
          lines: formData.lines.map(l => ({
            description: l.description,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: parseFloat(l.unit_price) || 0,
            tax_rate: parseFloat(l.tax_rate) || 0,
            vat_treatment: l.vat_treatment || 'standard',
            specifications: l.specifications || null,
            unit: l.unit || 'pcs'
          })),
          // New fields
          terms_conditions: formData.terms_conditions || null,
          warranty_clause: formData.warranty_clause || null,
          special_clauses: formData.special_clauses || null,
          discount_adjustment: parseFloat(formData.discount_adjustment) || 0,
          discount_adjustment_note: formData.discount_adjustment_note || null,
          payment_terms: formData.payment_terms || null,
          delivery_location: formData.delivery_location || null,
          project_reference: formData.project_reference || null,
          project_location: formData.project_location || null,
          layout_template: formData.layout_template || null,
          attachments: formData.attachments || [],
          signature: formData.signature || null
        })
      });
      
      if (res.ok) {
        toast.success('Purchase Order created');
        setShowPoModal(false);
        poDraft.clear();
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create PO');
      }
    } catch (err) {
      toast.error('Failed to create PO');
    }
  };

  // Edit PO - open modal with pre-filled data
  const handleEditPO = (po) => {
    setEditingPO(po);
    setFormData({
      supplier_id: po.supplier_id,
      date: po.date,
      valid_until: po.valid_until || '',
      expected_date: po.delivery_date || po.expected_date || '',
      delivery_required_by: po.delivery_required_by || '',
      po_id: '',
      vat_category: po.vat_category || 'standard',
      notes: po.notes || '',
      lines: po.lines && po.lines.length > 0
        ? po.lines.map(line => ({
            description: line.description || '',
            quantity: line.quantity ?? 0,
            unit_price: line.unit_price ?? 0,
            tax_rate: line.tax_rate || 5,
            vat_treatment: line.vat_treatment || (line.tax_rate === 0 ? 'zero_rated' : 'standard'),
            specifications: line.specifications || '',
            unit: line.unit || 'pcs'
          }))
        : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 5, vat_treatment: 'standard', specifications: '', unit: 'pcs' }],
      // New fields
      terms_conditions: po.terms_conditions || '',
      warranty_clause: po.warranty_clause || '',
      special_clauses: po.special_clauses || '',
      discount_adjustment: Number(po.discount_adjustment) || 0,
      discount_adjustment_note: po.discount_adjustment_note || '',
      payment_terms: po.payment_terms || '',
      delivery_location: po.delivery_location || '',
      project_reference: po.project_reference || '',
      project_location: po.project_location || '',
      layout_template: po.layout_template || '',
      attachments: po.attachments || [],
      signature: po.signature || ''
    });
    setShowPoModal(true);
  };

  // Update PO
  const handleUpdatePO = async (e) => {
    e.preventDefault();
    if (!editingPO) return;

    // If editing a PO that has moved beyond draft/rejected, ask for a reason
    // and use it to satisfy the backend's edit_after_approval_reason gate.
    const postApproval = editingPO.status && !['draft', 'rejected'].includes(editingPO.status);
    let reasonForEdit = '';
    if (postApproval) {
      reasonForEdit = window.prompt(
        `This LPO is currently "${editingPO.status}". Editing it will reset its status back to DRAFT and require a fresh approval.\n\nPlease enter the reason for editing:`,
        ''
      );
      if (!reasonForEdit || reasonForEdit.trim().length < 5) {
        toast.error('Edit cancelled — reason (min 5 chars) is required for post-approval edits');
        return;
      }
    }

    try {
      const res = await fetch(`${API}/api/purchase/orders/${editingPO.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          supplier_id: formData.supplier_id,
          date: formData.date,
          delivery_date: formData.expected_date || null,
          delivery_required_by: formData.delivery_required_by || null,
          vat_category: formData.vat_category,
          notes: formData.notes || null,
          lines: formData.lines.map(l => ({
            description: l.description,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: parseFloat(l.unit_price) || 0,
            tax_rate: parseFloat(l.tax_rate) || 0,
            vat_treatment: l.vat_treatment || 'standard',
            specifications: l.specifications || null,
            unit: l.unit || 'pcs'
          })),
          // New fields
          terms_conditions: formData.terms_conditions || null,
          warranty_clause: formData.warranty_clause || null,
          special_clauses: formData.special_clauses || null,
          discount_adjustment: parseFloat(formData.discount_adjustment) || 0,
          discount_adjustment_note: formData.discount_adjustment_note || null,
          payment_terms: formData.payment_terms || null,
          delivery_location: formData.delivery_location || null,
          project_reference: formData.project_reference || null,
          project_location: formData.project_location || null,
          layout_template: formData.layout_template || null,
          attachments: formData.attachments || [],
          signature: formData.signature || null,
          edit_after_approval_reason: reasonForEdit || null,
        })
      });
      
      if (res.ok) {
        toast.success('Purchase Order updated');
        setShowPoModal(false);
        setEditingPO(null);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update PO');
      }
    } catch (err) {
      toast.error('Failed to update PO');
    }
  };

  // Delete PO
  const handleDeletePO = async () => {
    if (!documentToDelete) return;
    const reason = (deletionReason || '').trim();
    if (reason.length < 5) {
      toast.error('Please enter a reason (min 5 characters) for deleting this LPO');
      return;
    }

    try {
      const res = await fetch(`${API}/api/purchase/orders/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deletion_reason: reason }),
      });

      if (res.ok) {
        toast.success('Purchase Order deleted');
        setShowDeleteConfirm(false);
        setDocumentToDelete(null);
        setDeletionReason('');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete PO');
      }
    } catch (err) {
      toast.error('Failed to delete PO');
    }
  };

  // GRN - Goods Receipt Note
  const handleCreateGrn = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/purchase/grn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          supplier_id: formData.supplier_id,
          po_id: formData.po_id || null,
          date: formData.date,
          notes: formData.notes || null,
          lines: formData.lines.map(l => ({
            description: l.description,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: parseFloat(l.unit_price) || 0,
            tax_rate: parseFloat(l.tax_rate) || 0
          }))
        })
      });
      
      if (res.ok) {
        toast.success('GRN created successfully');
        setShowGrnModal(false);
        grnDraft.clear();
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create GRN');
      }
    } catch (err) {
      toast.error('Failed to create GRN');
    }
  };

  const handleConvertRfqToPo = async (rfqId) => {
    try {
      const res = await fetch(`${API}/api/purchase/rfq/${rfqId}/convert-to-po`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('RFQ converted to Purchase Order');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to convert RFQ');
      }
    } catch (err) {
      toast.error('Failed to convert RFQ');
    }
  };

  // GRN Edit
  const handleEditGRN = (grn) => {
    setEditingGRN(grn);
    setFormData({
      supplier_id: grn.supplier_id,
      date: grn.receipt_date || new Date().toISOString().split('T')[0],
      valid_until: '',
      expected_date: '',
      po_id: grn.po_id,
      vat_category: 'standard',
      notes: grn.notes || '',
      lines: grn.lines && grn.lines.length > 0
        ? grn.lines.map(line => ({
            description: line.description || '',
            quantity: line.received_qty ?? line.quantity ?? 0,
            unit_price: line.unit_price ?? 0,
            tax_rate: line.tax_rate || 5
          }))
        : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 5 }]
    });
    setShowGrnModal(true);
  };

  // GRN Update
  const handleUpdateGRN = async (e) => {
    e.preventDefault();
    if (!editingGRN) return;
    
    try {
      const res = await fetch(`${API}/api/purchase/grn/${editingGRN.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          po_id: formData.po_id || editingGRN.po_id,
          receipt_date: formData.date,
          notes: formData.notes,
          lines: formData.lines.map(l => ({
            description: l.description,
            ordered_qty: l.quantity,
            received_qty: parseFloat(l.quantity) || 0,
            unit_price: parseFloat(l.unit_price) || 0
          }))
        })
      });
      
      if (res.ok) {
        toast.success('GRN updated successfully');
        setShowGrnModal(false);
        setEditingGRN(null);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update GRN');
      }
    } catch (err) {
      toast.error('Failed to update GRN');
    }
  };

  // GRN Delete
  const handleDeleteGRN = async () => {
    if (!documentToDelete) return;
    
    try {
      const res = await fetch(`${API}/api/purchase/grn/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('GRN deleted successfully');
        setShowDeleteConfirm(false);
        setDocumentToDelete(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete GRN');
      }
    } catch (err) {
      toast.error('Failed to delete GRN');
    }
  };

  const handleSubmitPo = async (poId) => {
    try {
      const res = await fetch(`${API}/api/purchase/orders/${poId}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('PO submitted for approval');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit PO');
      }
    } catch (err) {
      toast.error('Failed to submit PO');
    }
  };

  const handleReceivePo = async (poId) => {
    // Open GRN form pre-filled with PO data
    const po = orders.find(o => o.id === poId);
    if (po) {
      setFormData({
        ...formData,
        supplier_id: po.supplier_id,
        po_id: po.id,
        lines: po.lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_rate: l.tax_rate
        }))
      });
      setShowGrnModal(true);
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDetailModal(true);
  };

  const handleDownloadPdf = async (docType, docId) => {
    try {
      const response = await fetch(`${API}/api/purchase/${docType}/${docId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendEmail = (docType, doc) => {
    const supplier = suppliers.find(s => s.id === doc.supplier_id);
    setEmailModal({
      open: true,
      docType,
      docId: doc.id,
      docNumber: doc.document_number,
      supplierEmail: supplier?.email || '',
      supplierName: supplier?.name || doc.supplier_name || ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/10 text-gray-600',
      pending_approval: 'bg-amber-500/10 text-amber-600',
      approved: 'bg-blue-500/10 text-blue-600',
      ordered: 'bg-purple-500/10 text-purple-600',
      received: 'bg-emerald-500/10 text-emerald-600',
      invoiced: 'bg-teal-500/10 text-teal-600',
      cancelled: 'bg-red-500/10 text-red-600'
    };
    return <Badge className={styles[status] || styles.draft}>{status?.replace('_', ' ')}</Badge>;
  };

  // Filter function for search
  const filterDocuments = (docs) => {
    if (!searchTerm) return docs;
    const term = searchTerm.toLowerCase();
    return docs.filter(doc => 
      doc.document_number?.toLowerCase().includes(term) ||
      doc.supplier_name?.toLowerCase().includes(term) ||
      doc.notes?.toLowerCase().includes(term) ||
      doc.project_reference?.toLowerCase().includes(term) ||
      doc.project_location?.toLowerCase().includes(term)
    );
  };

  const statCards = [
    { label: 'RFQs', value: rfqs.length, icon: FileText, color: 'bg-blue-500/10 text-blue-600' },
    { label: 'Purchase Orders', value: orders.length, icon: ShoppingCart, color: 'bg-purple-500/10 text-purple-600' },
    { label: 'GRN', value: grns.length, icon: Package, color: 'bg-emerald-500/10 text-emerald-600' },
    { label: 'Pending Approval', value: orders.filter(o => o.status === 'pending_approval').length, icon: CheckCircle, color: 'bg-amber-500/10 text-amber-600' },
  ];

  const canCreate = user?.permissions?.hr?.includes('create');

  // Helper to render supplier selector inline with creation option
  const renderSupplierSelector = () => (
    <div className="col-span-2">
      <Label>Supplier *</Label>
      {!showNewSupplierInline ? (
        <div className="flex gap-2">
          <Select value={formData.supplier_id} onValueChange={(v) => setFormData(prev => ({...prev, supplier_id: v}))}>
            <SelectTrigger className="flex-1" data-testid="supplier-select">
              <SelectValue placeholder="Select supplier" />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" size="icon" onClick={() => setShowNewSupplierInline(true)} title="Add new supplier" data-testid="add-supplier-inline-btn">
            <UserPlus size={18} />
          </Button>
        </div>
      ) : (
        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">New Supplier</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewSupplierInline(false)}>Cancel</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input
              placeholder="Name *"
              value={newSupplier.name}
              onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
              data-testid="inline-supplier-name"
            />
            <Input
              placeholder="Company Name"
              value={newSupplier.company_name}
              onChange={(e) => setNewSupplier({...newSupplier, company_name: e.target.value})}
              data-testid="inline-supplier-company"
            />
            <Input
              type="email"
              placeholder="Email"
              value={newSupplier.email}
              onChange={(e) => setNewSupplier({...newSupplier, email: e.target.value})}
              data-testid="inline-supplier-email"
            />
            <Input
              placeholder="Phone"
              value={newSupplier.phone}
              onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})}
              data-testid="inline-supplier-phone"
            />
          </div>
          <Input
            placeholder="Address"
            value={newSupplier.address}
            onChange={(e) => setNewSupplier({...newSupplier, address: e.target.value})}
            data-testid="inline-supplier-address"
          />
          <Button type="button" size="sm" onClick={handleCreateSupplier} data-testid="save-inline-supplier-btn">
            <UserPlus size={14} className="mr-1" /> Create & Select
          </Button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="purchases-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="purchases-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Purchases
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage RFQs, Purchase Orders, and Goods Receipt
          </p>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card-tactile rounded-lg p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-full ${stat.color}`}>
                <stat.icon size={24} weight="duotone" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search by document number, supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="purchase-search"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="orders" data-testid="tab-orders">Purchase Orders</TabsTrigger>
            <TabsTrigger value="rfqs" data-testid="tab-rfqs">RFQs</TabsTrigger>
            <TabsTrigger value="grn" data-testid="tab-grn">GRN</TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2">
            {activeTab === 'rfqs' && canCreate && (
              <Button onClick={() => setShowRfqModal(true)} data-testid="new-rfq-btn">
                <Plus size={16} className="mr-2" /> New RFQ
              </Button>
            )}
            {activeTab === 'orders' && canCreate && (
              <Button onClick={() => setShowPoModal(true)} data-testid="new-po-btn">
                <Plus size={16} className="mr-2" /> New PO
              </Button>
            )}
            {activeTab === 'grn' && canCreate && (
              <Button onClick={() => setShowGrnModal(true)} data-testid="new-grn-btn">
                <Plus size={16} className="mr-2" /> New GRN
              </Button>
            )}
          </div>
        </div>

        {/* Purchase Orders Tab */}
        <TabsContent value="orders" className="mt-6">
          <DocSummary kind="purchase_orders" label="Purchase Orders" filters={{ q: searchTerm }} />
          <div className="card-tactile rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="hidden lg:table-cell">Project</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filterDocuments(orders).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No purchase orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filterDocuments(orders).map(po => (
                    <TableRow key={po.id} data-testid={`po-row-${po.id}`}>
                      <TableCell className="font-medium">{po.document_number}</TableCell>
                      <TableCell>{po.supplier_name}</TableCell>
                      <TableCell className="hidden lg:table-cell max-w-[180px] truncate" data-testid={`po-project-${po.id}`}>
                        {po.project_reference ? (
                          <div className="text-xs">
                            <div className="font-medium truncate">{po.project_reference}</div>
                            {po.project_location && <div className="text-muted-foreground truncate">{po.project_location}</div>}
                          </div>
                        ) : <span className="text-muted-foreground/60 text-xs">—</span>}
                      </TableCell>
                      <TableCell>{po.date}</TableCell>
                      <TableCell className="text-right">{formatCurrency(po.total)}</TableCell>
                      <TableCell>{getStatusBadge(po.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <RowActionsMenu
                            testid={`po-${po.id}`}
                            actions={[
                              { id: 'view', label: 'View', icon: Eye, onClick: () => handleViewDocument(po) },
                              { id: 'download-pdf', label: 'Download PDF', icon: DownloadSimple, onClick: () => handleDownloadPdf('orders', po.id) },
                              { id: 'email', label: 'Send Email', icon: PaperPlaneTilt, onClick: () => handleSendEmail('orders', po) },
                              (po.status === 'draft' || po.status === 'rejected') && 'separator',
                              po.status !== 'deleted' && !po.is_deleted && { id: 'edit', label: po.status === 'draft' || po.status === 'rejected' ? 'Edit' : 'Edit (resets to Draft)', icon: PencilSimple, onClick: () => handleEditPO(po) },
                              po.status === 'draft' && { id: 'submit', label: 'Submit for Approval', icon: PaperPlaneTilt, onClick: () => handleSubmitPo(po.id) },
                              po.status === 'approved' && { id: 'receive', label: 'Receive (Create GRN)', icon: Package, onClick: () => handleReceivePo(po.id) },
                              !po.is_deleted && 'separator',
                              !po.is_deleted && { id: 'delete', label: 'Delete', icon: Trash, onClick: () => { setDocumentToDelete(po); setDeleteDocType('po'); setDeletionReason(''); setShowDeleteConfirm(true); }, destructive: true },
                            ].filter(Boolean)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* RFQs Tab */}
        <TabsContent value="rfqs" className="mt-6">
          <DocSummary kind="purchase_rfqs" label="RFQs" filters={{ q: searchTerm }} />
          <div className="card-tactile rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>RFQ Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filterDocuments(rfqs).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No RFQs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filterDocuments(rfqs).map(rfq => (
                    <TableRow key={rfq.id} data-testid={`rfq-row-${rfq.id}`}>
                      <TableCell className="font-medium">{rfq.document_number}</TableCell>
                      <TableCell>{rfq.supplier_name}</TableCell>
                      <TableCell>{rfq.date}</TableCell>
                      <TableCell>{rfq.valid_until || '-'}</TableCell>
                      <TableCell>{getStatusBadge(rfq.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <RowActionsMenu
                            testid={`rfq-${rfq.id}`}
                            actions={[
                              { id: 'view', label: 'View', icon: Eye, onClick: () => handleViewDocument(rfq) },
                              { id: 'download-pdf', label: 'Download PDF', icon: DownloadSimple, onClick: () => handleDownloadPdf('rfq', rfq.id) },
                              { id: 'email', label: 'Send Email', icon: PaperPlaneTilt, onClick: () => handleSendEmail('rfq', rfq) },
                              (rfq.status === 'draft' || rfq.status === 'rejected') && 'separator',
                              (rfq.status === 'draft' || rfq.status === 'rejected') && { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEditRFQ(rfq) },
                              rfq.status === 'draft' && { id: 'convert', label: 'Convert to PO', icon: ArrowRight, onClick: () => handleConvertRfqToPo(rfq.id) },
                              (rfq.status === 'draft' || rfq.status === 'rejected') && 'separator',
                              (rfq.status === 'draft' || rfq.status === 'rejected') && { id: 'delete', label: 'Delete', icon: Trash, onClick: () => { setDocumentToDelete(rfq); setDeleteDocType('rfq'); setShowDeleteConfirm(true); }, destructive: true },
                            ].filter(Boolean)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* GRN Tab */}
        <TabsContent value="grn" className="mt-6">
          <DocSummary kind="grns" label="GRNs" filters={{ q: searchTerm }} />
          <div className="card-tactile rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>GRN Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>PO Ref</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filterDocuments(grns).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No GRN records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filterDocuments(grns).map(grn => (
                    <TableRow key={grn.id} data-testid={`grn-row-${grn.id}`}>
                      <TableCell className="font-medium">{grn.document_number}</TableCell>
                      <TableCell>{grn.supplier_name}</TableCell>
                      <TableCell>{grn.date}</TableCell>
                      <TableCell>{grn.po_number || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(grn.total)}</TableCell>
                      <TableCell>{getStatusBadge(grn.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <RowActionsMenu
                            testid={`grn-${grn.id}`}
                            actions={[
                              { id: 'view', label: 'View', icon: Eye, onClick: () => handleViewDocument(grn) },
                              { id: 'download-pdf', label: 'Download PDF', icon: DownloadSimple, onClick: () => handleDownloadPdf('grn', grn.id) },
                              { id: 'email', label: 'Send Email', icon: PaperPlaneTilt, onClick: () => handleSendEmail('grn', grn) },
                              (grn.status === 'draft' || grn.status === 'rejected') && 'separator',
                              (grn.status === 'draft' || grn.status === 'rejected') && { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEditGRN(grn) },
                              (grn.status === 'draft' || grn.status === 'rejected') && { id: 'delete', label: 'Delete', icon: Trash, onClick: () => { setDocumentToDelete(grn); setDeleteDocType('grn'); setShowDeleteConfirm(true); }, destructive: true },
                            ].filter(Boolean)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* RFQ Modal */}
      <Dialog open={showRfqModal} onOpenChange={(open) => {
        setShowRfqModal(open);
        if (!open) { setEditingRFQ(null); resetForm(); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editingRFQ ? `Edit RFQ - ${editingRFQ.document_number}` : 'New Request for Quotation'}
            </DialogTitle>
            <DialogDescription>
              {editingRFQ ? 'Update the RFQ details' : 'Request pricing from supplier for items you need'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingRFQ ? handleUpdateRFQ : handleCreateRfq} className="space-y-6 overflow-y-auto flex-1 pr-2 pb-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {!editingRFQ && (
              <DraftRestoreBanner
                visible={rfqDraft.hasDraft}
                savedAt={rfqDraft.draftSavedAt}
                label="RFQ"
                onRestore={() => { const r = rfqDraft.restore(); if (r) setFormData(r); }}
                onDiscard={rfqDraft.discard}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderSupplierSelector()}
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData(prev => ({...prev, date: e.target.value}))} 
                  required 
                  data-testid="rfq-date"
                />
              </div>
              <div>
                <Label>Valid Until</Label>
                <Input 
                  type="date" 
                  value={formData.valid_until} 
                  onChange={(e) => setFormData(prev => ({...prev, valid_until: e.target.value}))} 
                  data-testid="rfq-valid-until"
                />
              </div>
              <div>
                <Label>Delivery Required By</Label>
                <Input 
                  type="date" 
                  value={formData.delivery_required_by} 
                  onChange={(e) => setFormData(prev => ({...prev, delivery_required_by: e.target.value}))} 
                  data-testid="rfq-delivery-date"
                />
              </div>
            </div>

            <div>
              <Label>Delivery Location</Label>
              <Input 
                value={formData.delivery_location} 
                onChange={(e) => setFormData(prev => ({...prev, delivery_location: e.target.value}))} 
                placeholder="Delivery address or location"
                data-testid="rfq-delivery-location"
              />
            </div>

            {/* Line Items - RFQ with specifications and unit */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base">Items Required</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.lines.map((line, idx) => (
                  <div key={idx} className="p-3 bg-muted/30 rounded-lg space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-6">
                        <Label className="text-xs">Description *</Label>
                        <Input 
                          value={line.description} 
                          onChange={(e) => updateLine(idx, 'description', e.target.value)}
                          placeholder="Item description"
                          required
                          data-testid={`rfq-line-desc-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Specifications/Brand</Label>
                        <Input 
                          value={line.specifications || ''} 
                          onChange={(e) => updateLine(idx, 'specifications', e.target.value)}
                          placeholder="Brand/Specs"
                          data-testid={`rfq-line-specs-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Qty</Label>
                        <Input 
                          type="number" 
                          value={line.quantity} 
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                          min="0"
                          step="0.01"
                          data-testid={`rfq-line-qty-${idx}`}
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.lines.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                            <span className="text-destructive">×</span>
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-3">
                        <Label className="text-xs">Unit</Label>
                        <Select 
                          value={line.unit || 'pcs'} 
                          onValueChange={(v) => updateLine(idx, 'unit', v)}
                        >
                          <SelectTrigger data-testid={`rfq-line-unit-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pcs">Pieces (pcs)</SelectItem>
                            <SelectItem value="kg">Kilograms (kg)</SelectItem>
                            <SelectItem value="ltr">Liters (ltr)</SelectItem>
                            <SelectItem value="mtr">Meters (mtr)</SelectItem>
                            <SelectItem value="box">Box</SelectItem>
                            <SelectItem value="set">Set</SelectItem>
                            <SelectItem value="nos">Numbers (nos)</SelectItem>
                            <SelectItem value="unit">Unit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">* Pricing will be provided by the supplier</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Payment Terms</Label>
                <Input
                  list="rfq-payment-terms-presets"
                  value={formData.payment_terms || ''}
                  onChange={(e) => setFormData(prev => ({...prev, payment_terms: e.target.value}))}
                  placeholder="Type or pick a preset (e.g. 30% Advance, 70% on Delivery)"
                  data-testid="rfq-payment-terms"
                />
                <datalist id="rfq-payment-terms-presets">
                  <option value="100% Advance" />
                  <option value="50% Advance, 50% on Delivery" />
                  <option value="Cash on Delivery" />
                  <option value="Net 15 Days" />
                  <option value="Net 30 Days" />
                  <option value="Net 45 Days" />
                  <option value="Net 60 Days" />
                  <option value="Credit (as per agreement)" />
                </datalist>
              </div>
            </div>

            <div>
              <Label>Terms & Conditions</Label>
              <Textarea 
                value={formData.terms_conditions || ''} 
                onChange={(e) => setFormData(prev => ({...prev, terms_conditions: e.target.value}))} 
                rows={3} 
                placeholder="Enter terms and conditions..."
                data-testid="rfq-terms"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))} 
                rows={2} 
                placeholder="Additional requirements or specifications..."
                data-testid="rfq-notes"
              />
            </div>

            {/* Signature Pad */}
            <div className="pb-2">
              <SignaturePad
                value={formData.signature}
                onChange={(sig) => setFormData(prev => ({...prev, signature: sig}))}
                label="Authorized Signature"
              />
            </div>

            <div>
              <Label>PDF Layout</Label>
              <div className="flex items-center gap-2">
                <select
                  value={formData.layout_template || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, layout_template: e.target.value }))}
                  className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  data-testid="rfq-layout-template"
                >
                  <option value="">Use company default</option>
                  <option value="classic">Classic — split header</option>
                  <option value="modern_band">Modern Band — coloured header</option>
                  <option value="minimalist">Minimalist — black &amp; white</option>
                  <option value="compact">Compact — tighter spacing</option>
                </select>
                <LayoutPreviewButton layout={formData.layout_template || 'classic'} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-background pb-2 border-t mt-4">
              {!editingRFQ && <div className="mr-auto"><DraftSavedIndicator savedAt={rfqDraft.lastAutoSavedAt} /></div>}
              <Button type="button" variant="outline" onClick={() => { setShowRfqModal(false); setEditingRFQ(null); resetForm(); }}>Cancel</Button>
              <Button type="submit" data-testid="submit-rfq-btn">{editingRFQ ? 'Update RFQ' : 'Create RFQ'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PO Modal */}
      <Dialog open={showPoModal} onOpenChange={(open) => { 
        setShowPoModal(open); 
        if (!open) { setEditingPO(null); resetForm(); }
      }}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editingPO ? `Edit Purchase Order - ${editingPO.document_number}` : 'New Purchase Order'}
            </DialogTitle>
            <DialogDescription>
              {editingPO ? 'Update purchase order details' : 'Create a purchase order with confirmed pricing'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingPO ? handleUpdatePO : handleCreatePo} className="space-y-6 overflow-y-auto flex-1 pr-2 pb-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {!editingPO && (
              <DraftRestoreBanner
                visible={poDraft.hasDraft}
                savedAt={poDraft.draftSavedAt}
                label="purchase order"
                onRestore={() => { const r = poDraft.restore(); if (r) setFormData(r); }}
                onDiscard={poDraft.discard}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderSupplierSelector()}
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData(prev => ({...prev, date: e.target.value}))} 
                  required 
                  data-testid="po-date"
                />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input 
                  type="date" 
                  value={formData.expected_date} 
                  onChange={(e) => setFormData(prev => ({...prev, expected_date: e.target.value}))} 
                  data-testid="po-expected-date"
                />
              </div>
              <div>
                <Label>Delivery Required By</Label>
                <Input
                  type="date"
                  value={formData.delivery_required_by || ''}
                  onChange={(e) => setFormData(prev => ({...prev, delivery_required_by: e.target.value}))}
                  data-testid="po-delivery-required-by"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Our required-by date. Distinct from supplier&apos;s promised expected date.</p>
              </div>
              <div>
                <Label>VAT Category (UAE)</Label>
                <Select 
                  value={formData.vat_category} 
                  onValueChange={(v) => {
                    const taxRate = v === 'standard' ? 5 : 0;
                    setFormData(prev => ({
                      ...prev, 
                      vat_category: v,
                      lines: prev.lines.map(l => ({ ...l, tax_rate: taxRate }))
                    }));
                  }}
                >
                  <SelectTrigger data-testid="po-vat-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Rated (5%)</SelectItem>
                    <SelectItem value="zero_rated">Zero Rated (0%)</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                    <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                    <SelectItem value="not_yet_due">Not Yet Due (Deferred VAT)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.vat_category && formData.vat_category !== 'standard' && (
                  <p className="text-[11px] mt-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 px-2 py-1" data-testid="po-vat-zero-confirm">
                    No VAT applied — total = subtotal − discounts.
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label>Delivery Location</Label>
              <Input 
                value={formData.delivery_location || ''} 
                onChange={(e) => setFormData(prev => ({...prev, delivery_location: e.target.value}))} 
                placeholder="Delivery address or location"
                data-testid="po-delivery-location"
              />
            </div>

            {/* Line Items with pricing, specifications, unit */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus size={14} className="mr-1" /> Add Line
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.lines.map((line, idx) => (
                  <div key={idx} className="p-3 bg-muted/30 rounded-lg space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <Label className="text-xs">Description *</Label>
                        <Input 
                          value={line.description} 
                          onChange={(e) => updateLine(idx, 'description', e.target.value)}
                          placeholder="Item description"
                          required
                          data-testid={`po-line-desc-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Specifications/Brand</Label>
                        <Input 
                          value={line.specifications || ''} 
                          onChange={(e) => updateLine(idx, 'specifications', e.target.value)}
                          placeholder="Brand/Specs"
                          data-testid={`po-line-specs-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Unit</Label>
                        <Select 
                          value={line.unit || 'pcs'} 
                          onValueChange={(v) => updateLine(idx, 'unit', v)}
                        >
                          <SelectTrigger data-testid={`po-line-unit-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pcs">pcs</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="ltr">ltr</SelectItem>
                            <SelectItem value="mtr">mtr</SelectItem>
                            <SelectItem value="box">box</SelectItem>
                            <SelectItem value="set">set</SelectItem>
                            <SelectItem value="nos">nos</SelectItem>
                            <SelectItem value="unit">unit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Qty</Label>
                        <Input 
                          type="number" 
                          value={line.quantity} 
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                          min="0"
                          step="0.01"
                          data-testid={`po-line-qty-${idx}`}
                        />
                      </div>
                      <div className="col-span-1">
                        {formData.lines.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                            <span className="text-destructive">×</span>
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-3">
                        <Label className="text-xs">Unit Price</Label>
                        <Input 
                          type="number" 
                          value={line.unit_price} 
                          onChange={(e) => updateLine(idx, 'unit_price', e.target.value)}
                          min="0"
                          step="0.01"
                          data-testid={`po-line-price-${idx}`}
                        />
                      </div>
                      <div className="col-span-5">
                        <Label className="text-xs">VAT treatment</Label>
                        <Select
                          value={line.vat_treatment || 'standard'}
                          onValueChange={(val) => {
                            const updated = applyVatTreatment(line, val);
                            setFormData(prev => ({
                              ...prev,
                              lines: prev.lines.map((l, i) => i === idx ? updated : l),
                            }));
                          }}
                        >
                          <SelectTrigger data-testid={`po-line-vat-treatment-${idx}`} className="h-9 text-xs w-full">
                            <SelectValue placeholder="Select VAT treatment" />
                          </SelectTrigger>
                          <SelectContent className="min-w-[240px]">
                            {VAT_TREATMENT_OPTIONS.map(o => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {line.vat_treatment === 'custom' && (
                          <Input
                            type="number"
                            value={line.tax_rate}
                            onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)}
                            min="0"
                            max="100"
                            className="mt-1 h-8 text-xs"
                            placeholder="Enter custom %"
                            data-testid={`po-line-tax-custom-${idx}`}
                          />
                        )}
                      </div>
                      <div className="col-span-4 flex items-end">
                        <span className="text-sm text-muted-foreground">
                          Line Total: {formatCurrency(computeLineTotal(line))}
                          {line.vat_treatment === 'inclusive' && (
                            <span className="ml-1 text-[10px] text-amber-700">(VAT incl.)</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">Total: </span>
                  <span className="text-lg font-semibold">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Payment Terms</Label>
                <Input
                  list="po-payment-terms-presets"
                  value={formData.payment_terms || ''}
                  onChange={(e) => setFormData(prev => ({...prev, payment_terms: e.target.value}))}
                  placeholder="Type or pick a preset (e.g. 30% Advance, 70% on Delivery)"
                  data-testid="po-payment-terms"
                />
                <datalist id="po-payment-terms-presets">
                  <option value="100% Advance" />
                  <option value="50% Advance, 50% on Delivery" />
                  <option value="Cash on Delivery" />
                  <option value="Net 15 Days" />
                  <option value="Net 30 Days" />
                  <option value="Net 45 Days" />
                  <option value="Net 60 Days" />
                  <option value="Credit (as per agreement)" />
                </datalist>
              </div>
            </div>

            {/* Project Reference & Location (searchable in summary) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Project Reference</Label>
                <Input
                  value={formData.project_reference || ''}
                  onChange={(e) => setFormData(prev => ({...prev, project_reference: e.target.value}))}
                  placeholder="e.g., PRJ-2026-001"
                  data-testid="po-project-reference"
                />
              </div>
              <div>
                <Label>Project Location</Label>
                <Input
                  value={formData.project_location || ''}
                  onChange={(e) => setFormData(prev => ({...prev, project_location: e.target.value}))}
                  placeholder="e.g., Dubai Marina"
                  data-testid="po-project-location"
                />
              </div>
            </div>

            <div>
              <Label>Terms & Conditions</Label>
              <Textarea 
                value={formData.terms_conditions || ''} 
                onChange={(e) => setFormData(prev => ({...prev, terms_conditions: e.target.value}))} 
                rows={3} 
                placeholder="Enter terms and conditions..."
                data-testid="po-terms"
              />
            </div>

            <div>
              <Label>Warranty Clause</Label>
              <Textarea
                value={formData.warranty_clause || ''}
                onChange={(e) => setFormData(prev => ({...prev, warranty_clause: e.target.value}))}
                rows={2}
                placeholder="e.g. 12 months warranty against manufacturing defects from date of delivery..."
                data-testid="po-warranty"
              />
            </div>

            <div>
              <Label>Special Clauses</Label>
              <Textarea
                value={formData.special_clauses || ''}
                onChange={(e) => setFormData(prev => ({...prev, special_clauses: e.target.value}))}
                rows={2}
                placeholder="Penalty for late delivery, escalation, force majeure, etc."
                data-testid="po-special-clauses"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Discount / Adjustment</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.discount_adjustment ?? 0}
                  onChange={(e) => setFormData(prev => ({...prev, discount_adjustment: e.target.value}))}
                  placeholder="0.00"
                  data-testid="po-discount-adjustment"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Subtracted from PO total. Useful when adjusting against an earlier quote, supplier discount, or partial advance.
                </p>
              </div>
              <div>
                <Label>Adjustment Note</Label>
                <Input
                  value={formData.discount_adjustment_note || ''}
                  onChange={(e) => setFormData(prev => ({...prev, discount_adjustment_note: e.target.value}))}
                  placeholder="e.g. Advance against QTN-0042 / Loyalty discount"
                  data-testid="po-discount-note"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))} 
                rows={2} 
                data-testid="po-notes"
              />
            </div>

            {/* Attachments — supplier quote / supporting docs.
                Backend route: POST /api/documents/attachments/purchase_orders/{po_id}
                For NEW POs, the user must create the PO first, then attach files. */}
            {editingPO ? (
              <div className="rounded-md border border-input p-3 bg-card">
                <Label className="mb-2 block">Attachments (Supplier Quote / References)</Label>
                <QuotationAttachmentField documentId={editingPO.id} documentType="po" />
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-input p-3 bg-muted/30 text-xs text-muted-foreground">
                <strong>Supplier Quote / Attachments:</strong> Save this PO first, then re-open from the list to attach the supplier&apos;s quote PDF and other supporting documents as references.
              </div>
            )}

            {/* Signature Pad */}
            <div className="pb-2">
              <SignaturePad
                value={formData.signature}
                onChange={(sig) => setFormData(prev => ({...prev, signature: sig}))}
                label="Authorized Signature"
              />
            </div>

            <div>
              <Label>PDF Layout</Label>
              <div className="flex items-center gap-2">
                <select
                  value={formData.layout_template || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, layout_template: e.target.value }))}
                  className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  data-testid="po-layout-template"
                >
                  <option value="">Use company default</option>
                  <option value="classic">Classic — split header</option>
                  <option value="modern_band">Modern Band — coloured header</option>
                  <option value="minimalist">Minimalist — black &amp; white</option>
                  <option value="compact">Compact — tighter spacing</option>
                </select>
                <LayoutPreviewButton layout={formData.layout_template || 'classic'} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-background pb-2 border-t mt-4">
              {!editingPO && <div className="mr-auto"><DraftSavedIndicator savedAt={poDraft.lastAutoSavedAt} /></div>}
              <Button type="button" variant="outline" onClick={() => { setShowPoModal(false); setEditingPO(null); resetForm(); }}>Cancel</Button>
              <Button type="submit" data-testid="submit-po-btn">{editingPO ? 'Update PO' : 'Create PO'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={(v) => { setShowDeleteConfirm(v); if (!v) setDeletionReason(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash className="w-5 h-5" />
              Delete {deleteDocType === 'rfq' ? 'RFQ' : deleteDocType === 'grn' ? 'GRN' : 'Purchase Order'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {documentToDelete?.document_number}? This action is a soft-delete and is fully audit-logged.
            </DialogDescription>
          </DialogHeader>
          {deleteDocType === 'po' && (
            <div className="space-y-2 py-2">
              <Label htmlFor="deletion-reason" className="text-sm font-medium">Reason for deletion <span className="text-red-500">*</span></Label>
              <textarea
                id="deletion-reason"
                data-testid="po-delete-reason"
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]"
                placeholder="e.g. Supplier cancelled, duplicate entry, wrong customer, etc."
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Minimum 5 characters. Written to audit_logs.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeletionReason(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={deleteDocType === 'rfq' ? handleDeleteRFQ : deleteDocType === 'grn' ? handleDeleteGRN : handleDeletePO}
              data-testid="confirm-delete"
              disabled={deleteDocType === 'po' && (deletionReason.trim().length < 5)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GRN Modal */}
      <Dialog open={showGrnModal} onOpenChange={(open) => {
        setShowGrnModal(open);
        if (!open) { setEditingGRN(null); resetForm(); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGRN ? `Edit GRN - ${editingGRN.document_number}` : 'New Goods Receipt Note'}
            </DialogTitle>
            <DialogDescription>
              {editingGRN ? 'Update the goods receipt details' : 'Record goods received from supplier'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingGRN ? handleUpdateGRN : handleCreateGrn} className="space-y-6">
            {!editingGRN && (
              <DraftRestoreBanner
                visible={grnDraft.hasDraft}
                savedAt={grnDraft.draftSavedAt}
                label="GRN"
                onRestore={() => { const r = grnDraft.restore(); if (r) setFormData(r); }}
                onDiscard={grnDraft.discard}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderSupplierSelector()}
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData(prev => ({...prev, date: e.target.value}))} 
                  required 
                  data-testid="grn-date"
                />
              </div>
              <div className="col-span-2">
                <Label>Against PO (Optional)</Label>
                <Select value={formData.po_id || 'standalone'} onValueChange={(v) => {
                  if (v === 'standalone') {
                    setFormData(prev => ({...prev, po_id: ''}));
                  } else {
                    const po = orders.find(o => o.id === v);
                    if (po) {
                      setFormData(prev => ({
                        ...prev,
                        po_id: v,
                        supplier_id: po.supplier_id,
                        lines: po.lines.map(l => ({
                          description: l.description,
                          quantity: l.quantity,
                          unit_price: l.unit_price,
                          tax_rate: l.tax_rate
                        }))
                      }));
                    } else {
                      setFormData(prev => ({...prev, po_id: v}));
                    }
                  }
                }}>
                  <SelectTrigger data-testid="grn-po-select">
                    <SelectValue placeholder="Select PO or leave empty for standalone GRN" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standalone">Standalone GRN (no PO)</SelectItem>
                    {orders.filter(o => o.status === 'approved' || o.status === 'ordered').map(po => (
                      <SelectItem key={po.id} value={po.id}>{po.document_number} - {po.supplier_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base">Received Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus size={14} className="mr-1" /> Add Line
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.lines.map((line, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-muted/30 rounded-lg">
                    <div className="col-span-5">
                      <Label className="text-xs">Description</Label>
                      <Input 
                        value={line.description} 
                        onChange={(e) => updateLine(idx, 'description', e.target.value)}
                        placeholder="Item description"
                        required
                        data-testid={`grn-line-desc-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Qty Received</Label>
                      <Input 
                        type="number" 
                        value={line.quantity} 
                        onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                        min="0"
                        step="0.01"
                        data-testid={`grn-line-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Unit Cost</Label>
                      <Input 
                        type="number" 
                        value={line.unit_price} 
                        onChange={(e) => updateLine(idx, 'unit_price', e.target.value)}
                        min="0"
                        step="0.01"
                        data-testid={`grn-line-price-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">VAT</Label>
                      <Select
                        value={line.vat_treatment || 'standard'}
                        onValueChange={(val) => {
                          const updated = applyVatTreatment(line, val);
                          setFormData(prev => ({
                            ...prev,
                            lines: prev.lines.map((l, i) => i === idx ? updated : l),
                          }));
                        }}
                      >
                        <SelectTrigger data-testid={`grn-line-vat-treatment-${idx}`} className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VAT_TREATMENT_OPTIONS.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {line.vat_treatment === 'custom' && (
                        <Input
                          type="number"
                          value={line.tax_rate}
                          onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)}
                          min="0"
                          max="100"
                          className="mt-1 h-8 text-xs"
                          placeholder="%"
                          data-testid={`grn-line-tax-custom-${idx}`}
                        />
                      )}
                    </div>
                    <div className="col-span-1">
                      {formData.lines.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                          <span className="text-destructive">×</span>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">Total: </span>
                  <span className="text-lg font-semibold">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))} 
                rows={2} 
                placeholder="Delivery notes, condition of goods, etc."
                data-testid="grn-notes"
              />
            </div>

            <div>
              <Label>PDF Layout</Label>
              <div className="flex items-center gap-2">
                <select
                  value={formData.layout_template || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, layout_template: e.target.value }))}
                  className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  data-testid="grn-layout-template"
                >
                  <option value="">Use company default</option>
                  <option value="classic">Classic — split header</option>
                  <option value="modern_band">Modern Band — coloured header</option>
                  <option value="minimalist">Minimalist — black &amp; white</option>
                  <option value="compact">Compact — tighter spacing</option>
                </select>
                <LayoutPreviewButton layout={formData.layout_template || 'classic'} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              {!editingGRN && <div className="mr-auto"><DraftSavedIndicator savedAt={grnDraft.lastAutoSavedAt} /></div>}
              <Button type="button" variant="outline" onClick={() => { setShowGrnModal(false); setEditingGRN(null); resetForm(); }}>Cancel</Button>
              <Button type="submit" data-testid="submit-grn-btn">{editingGRN ? 'Update GRN' : 'Create GRN'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Detail/Preview Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedDocument?.document_number}</span>
              {getStatusBadge(selectedDocument?.status)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDocument && (
            <div className="space-y-6">
              {/* Company Header with Logo */}
              <div className="flex justify-between items-start border-b pb-4">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img src={logoUrl} alt={company?.name} className="h-12 w-auto" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                      <span className="text-primary-foreground font-bold text-lg">{company?.name?.[0] || 'E'}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">{company?.name || 'Company'}</h3>
                    <p className="text-sm text-muted-foreground">{company?.address || ''}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{selectedDocument.document_number}</p>
                  <p className="text-sm text-muted-foreground">Date: {selectedDocument.date}</p>
                </div>
              </div>

              {/* Supplier Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Supplier</h4>
                  <p className="font-medium">{selectedDocument.supplier_name}</p>
                </div>
                {selectedDocument.expected_date && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Expected Delivery</h4>
                    <p>{selectedDocument.expected_date}</p>
                  </div>
                )}
                {selectedDocument.valid_until && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Valid Until</h4>
                    <p>{selectedDocument.valid_until}</p>
                  </div>
                )}
              </div>

              {/* Line Items */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    {selectedDocument.document_type !== 'rfq' && (
                      <>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Tax</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedDocument.lines?.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell className="text-right">{line.quantity}</TableCell>
                      {selectedDocument.document_type !== 'rfq' && (
                        <>
                          <TableCell className="text-right">{formatCurrency(line.unit_price)}</TableCell>
                          <TableCell className="text-right">{line.tax_rate}%</TableCell>
                          <TableCell className="text-right">{formatCurrency(line.line_total || (line.quantity * line.unit_price))}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totals */}
              {selectedDocument.document_type !== 'rfq' && (
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatCurrency(selectedDocument.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tax</span>
                      <span>{formatCurrency(selectedDocument.tax_total)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg border-t pt-2">
                      <span>Total</span>
                      <span>{formatCurrency(selectedDocument.total)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedDocument.notes && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Notes</h4>
                  <p className="text-sm">{selectedDocument.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
            <Button onClick={() => handleDownloadPdf(selectedDocument?.document_type === 'rfq' ? 'rfq' : selectedDocument?.document_type === 'purchase_order' ? 'orders' : 'grn', selectedDocument?.id)}>
              <DownloadSimple size={16} className="mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <SendEmailModal
        open={emailModal.open}
        onClose={() => setEmailModal(m => ({ ...m, open: false }))}
        docType={emailModal.docType}
        docId={emailModal.docId}
        docNumber={emailModal.docNumber}
        supplierEmail={emailModal.supplierEmail}
        supplierName={emailModal.supplierName}
        companyName={company?.name || ''}
      />
    </div>
  );
}
